import {message} from "antd";
import {useContext, useEffect, useRef} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {SbomComponentServiceContext} from "../../Contexts";
import SbomComponent from "../../domain/SbomComponent";
import {FOCUS_COMPONENT_PARAM, FOCUS_FINDING_PARAM} from "../../domain/SbomUiUrls";
import {FocusedFindingAnalysisUrlSyncController} from "./useFocusedFindingAnalysis";

interface FocusedFindingUrlSyncOptions {
    projectVersionId?: string;
    analysis: FocusedFindingAnalysisUrlSyncController;
}

export function useFocusedFindingUrlSync(options: FocusedFindingUrlSyncOptions) {
    const sbomComponentService = useContext(SbomComponentServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const analysisRef = useRef(options.analysis);
    const locationRef = useRef(location);
    const projectVersionIdRef = useRef(options.projectVersionId);
    const state = options.analysis.state;

    useEffect(() => {
        analysisRef.current = options.analysis;
    });

    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    useEffect(() => {
        projectVersionIdRef.current = options.projectVersionId;
    }, [options.projectVersionId]);

    useEffect(() => {
        restoreFocusedFindingModalFromUrl();
    }, [location.search, options.projectVersionId]);

    useEffect(() => {
        const analysis = analysisRef.current;

        if (analysis.isRestorePending()) {
            return;
        }

        updateFocusedFindingUrl(state?.component?.id, state?.findingId);
    }, [state?.component?.id, state?.findingId]);

    function restoreFocusedFindingModalFromUrl() {
        const params = new URLSearchParams(locationRef.current.search);
        const componentId = params.get(FOCUS_COMPONENT_PARAM);
        const analysis = analysisRef.current;
        const requestId = analysis.beginRestore();

        if (componentId === null) {
            analysis.finishRestore();

            if (analysis.state !== undefined) {
                analysis.updateModal(undefined);
                analysis.clearNavigationComponents();
            }

            return;
        }

        const findingId = params.get(FOCUS_FINDING_PARAM) ?? undefined;

        if (analysis.state?.component?.id === componentId && analysis.state.findingId === findingId) {
            analysis.finishRestore();

            return;
        }

        sbomComponentService.get(componentId)
            .then(component => {
                if (!analysis.isRestoreCurrent(requestId)) {
                    return;
                }

                if (component.projectVersion?.id !== projectVersionIdRef.current) {
                    analysis.updateModal(undefined);
                    analysis.clearNavigationComponents();
                    updateFocusedFindingUrl(undefined, undefined);

                    return;
                }

                analysis.updateModal({component, findingId});
                analysis.reloadNavigationComponentsForComponent(component.id);

                if (findingId !== undefined) {
                    return analysis.reloadFocusedFindingPage(component.id, findingId)
                        .then(findingsPage => {
                            if (!analysis.isRestoreCurrent(requestId)) {
                                return;
                            }

                            if (findingsPage?.data.some(value => value.id === findingId)) {
                                return;
                            }

                            return restoreFirstFocusedFinding(component, requestId);
                        });
                }

                return restoreFirstFocusedFinding(component, requestId);
            })
            .catch(() => {
                if (!analysis.isRestoreCurrent(requestId)) {
                    return;
                }

                message.error("Cannot restore vulnerability analysis view.");

                analysis.updateModal(undefined);
                analysis.clearNavigationComponents();
                updateFocusedFindingUrl(undefined, undefined);
            })
            .finally(() => {
                if (!analysis.isRestoreCurrent(requestId)) {
                    return;
                }

                analysis.finishRestore();
            });
    }

    function restoreFirstFocusedFinding(component: SbomComponent, requestId: number) {
        const analysis = analysisRef.current;

        return analysis.reloadComponentFindings(component.id)
            .then(findingsPage => {
                if (!analysis.isRestoreCurrent(requestId)) {
                    return;
                }

                const firstFindingId = findingsPage?.data[0]?.id;

                analysis.updateModal({component, findingId: firstFindingId});
                updateFocusedFindingUrl(component.id, firstFindingId);
            });
    }

    function updateFocusedFindingUrl(componentId?: string, findingId?: string) {
        const currentLocation = locationRef.current;
        const params = new URLSearchParams(currentLocation.search);

        if (componentId === undefined) {
            params.delete(FOCUS_COMPONENT_PARAM);
            params.delete(FOCUS_FINDING_PARAM);
        } else {
            params.set(FOCUS_COMPONENT_PARAM, componentId);

            if (findingId === undefined) {
                params.delete(FOCUS_FINDING_PARAM);
            } else {
                params.set(FOCUS_FINDING_PARAM, findingId);
            }
        }

        const search = params.toString();
        const target = `${currentLocation.pathname}${search === "" ? "" : `?${search}`}${currentLocation.hash}`;
        const current = `${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}`;

        if (target !== current) {
            navigate(target, {replace: true});
        }
    }
}
